import { capitalizeFirstLetter } from "@repo/mfe-core";
import {
  Box,
  ApplicationIcon,
  Typography,
  Tooltip,
  Icon,
  IconKind,
} from "@saviynt/design-system";
import classNames from "classnames";
import { IntlShape } from "react-intl";
import { LinkCell } from "../../PoliciesPageList/CellRenderer/CellRenderer";
import { getApplicationSourceIcon } from "../../PoliciesSidepanel/PoliciesSidepanel";
import {
  PolicyFindingDetail,
  PolicyFindingRemediationData,
} from "@repo/api/schemas/policiesFindings.schema";
import ActionButton from "../ActionButton/ActionButton";

export function IdentityCell({
  row,
}: {
  row: {
    nativeSource: string;
    identityDisplayName: string;
    identityName: string;
    nativeType: string;
  };
}) {
  return (
    <div className="flex identity-name-cell">
      <Box className="pr-2">
        <ApplicationIcon logo={getApplicationSourceIcon(row.nativeSource)} />
      </Box>
      <Box>
        <Typography
          kind="body2"
          color="--color-foreground-primary-medium"
          lineLimit={1}
          tooltipConfigs={{ placement: "left" }}
        >
          {row.identityDisplayName || row.identityName}
        </Typography>
        {row.nativeType && (
          <Typography
            kind="body2"
            color="--color-foreground-neutral-subtle"
            lineLimit={1}
            tooltipConfigs={{ placement: "left" }}
          >
            {row.nativeType}
          </Typography>
        )}
      </Box>
    </div>
  );
}

export function RemediationCell({
  remediationData,
  intl,
}: {
  remediationData: PolicyFindingRemediationData[];
  intl: IntlShape;
}) {
  if (remediationData?.length === 0)
    return (
      <Typography kind="body2" desktop="body2-d" mobile="body2-d">
        {String("-")}
      </Typography>
    );

  return (
    <>
      {remediationData.map((remediation) =>
        remediation.identifier ? (
          <Box
            className="flex items-center gap-2 mb-1"
            key={remediation.identifier}
          >
            <Tooltip
              trigger={
                <Box className="flex items-center gap-2">
                  <Icon
                    kind="StatusCompleteFill"
                    label={intl.formatMessage({
                      defaultMessage: "Ticket Status",
                      description: "Status icon label",
                      id: "wAl2V7",
                    })}
                    color={
                      remediation.externalStatus?.toLowerCase() === "closed"
                        ? "--color-foreground-success-subtle"
                        : "--color-foreground-warning-subtle"
                    }
                    size="xxSmall"
                    className="Policy-remediation-ticket-status"
                  />
                  <ApplicationIcon
                    logo={
                      remediation.remediationTarget === "servicenow"
                        ? ("ServiceNow" as IconKind)
                        : ("Saviynt" as IconKind)
                    }
                    size="xxSmall"
                  />
                </Box>
              }
              text={
                remediation.remediationTarget === "servicenow"
                  ? `ServiceNow Ticket \n Status: ${capitalizeFirstLetter(remediation.externalStatus)}`
                  : `IGA Request \n Status: ${capitalizeFirstLetter(remediation.externalStatus)}`
              }
              placement="top-start"
            />
            <LinkCell
              text={
                remediation.actionType !== "create_ticket"
                  ? `REQ${remediation.identifier}`
                  : remediation.identifier
              }
              onClick={(e) => {
                e.stopPropagation();
                window.open(remediation.hyperlink, "_blank");
              }}
            />
          </Box>
        ) : null,
      )}
    </>
  );
}

export function ActionCell({
  row,
  remediationPermission,
}: {
  row: PolicyFindingDetail;
  remediationPermission: boolean;
}) {
  return (
    <div
      className={classNames("flex", "gap-2", {
        "justify-center items-center": row?.allowedActions?.length > 0,
      })}
    >
      {remediationPermission && <ActionButton rowData={row} />}
    </div>
  );
}
